import { say } from '@/lib/vocabulary';
import { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { backend } from '@/api/backend';
import { explainLine } from '@/api/explain';
import { ApiError } from '@/api/http';
import { Button, EmptyState, Panel, PanelHeader, Skeleton, Stamp, Loading } from '@/components/ui';
import { ToolIcon } from '@/components/ToolIcon';

const retry = (n: number, e: unknown) => (e instanceof ApiError ? e.retryable && n < 2 : n < 2);

/** Matches read per request. Asked for by name so the button can promise it. */
const PAGE = 50;

/**
 * Grounded record search inside the bucket you are working in. Results are
 * published reconstructed records only; an EMPTY index is stated as such,
 * never padded.
 *
 * This searched one scope and let you pick which, from a dropdown of every
 * registered bucket in the account, defaulted to whichever came back first.
 * So arriving here from a bucket put you in a different bucket's records
 * without saying so, and the control that did it was the only way to get
 * back. The scope is the URL now: the bucket is chosen once, upstream, and
 * every surface underneath it - this one included - is that bucket's.
 */
export function SearchLive() {
  const { bucketId } = useParams();
  const box = useRef<HTMLInputElement>(null);
  useEffect(() => {
    box.current?.focus();
  }, []);
  const bucket = useQuery({
    queryKey: ['flow-bucket', bucketId],
    queryFn: ({ signal }) => backend.getBucket(bucketId!, signal),
    enabled: Boolean(bucketId),
    retry,
  });
  const [text, setText] = useState('');
  const [q, setQ] = useState('');

  useEffect(() => {
    const t = setTimeout(() => setQ(text.trim()), 350);
    return () => clearTimeout(t);
  }, [text]);

  /**
   * Paged, because the server mints a cursor whenever more match.
   *
   * This read one page and then printed "and more match this search" under it,
   * with no control that could reach them - the search told you it was holding
   * something back and gave you no way to ask for it. `searchBucket` already
   * took a cursor; nothing passed one. The manifest browser has done keyset
   * paging correctly since it was written, so this is that pattern, not a new
   * one.
   */
  const results = useInfiniteQuery({
    queryKey: ['search', bucketId, q],
    initialPageParam: undefined as string | undefined,
    queryFn: ({ pageParam, signal }) => backend.searchBucket(bucketId!, q, { cursor: pageParam, pageSize: PAGE }, signal),
    // An absent cursor is the end of the ranked window. Reading it as "ask
    // again" would re-request the last page forever.
    getNextPageParam: (last) => last.nextCursor ?? undefined,
    enabled: Boolean(bucketId && q.length >= 2),
    retry,
    staleTime: 15_000,
  });

  if (bucket.isPending) return <Loading what="Reading this bucket" rows={4} />;

  if (bucket.isError || !bucket.data)
    return (
      <EmptyState
        title="This bucket is unavailable"
        description={explainLine(bucket.error, 'read this bucket')}
      />
    );

  // The first page carries the facts about the window - availability, the
  // ranking rule, how many matched. Every page after it carries more items.
  const r = results.data?.pages[0];
  const matches = (results.data?.pages ?? []).flatMap((p) => p.items);
  return (
    <div className="min-h-0 flex-1 animate-pop space-y-3 overflow-y-auto p-5">
      <h1 className="text-h2 text-ink">Search this bucket</h1>
      <p className="text-small text-ink-3">
        Published reconstructed records in <span className="mono text-ink-2">{bucket.data.bucket.name}</span>.
      </p>
      <Panel>
        <div className="p-3">
          <input
            ref={box}
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder="Search reconstructed records…"
            className="w-full rounded border border-line bg-panel-2 px-3 py-2 text-body text-ink outline-none placeholder:text-ink-4 focus:border-acc-2"
          />
        </div>
      </Panel>

      {q.length < 2 ? (
        <p className="px-1 text-small text-ink-3">Type at least two characters. Every result is a published reconstructed record with evidence.</p>
      ) : results.isPending ? (
        <div className="space-y-2">{Array.from({ length: 3 }).map((_, i) => <Skeleton key={i} className="h-10 w-full" />)}</div>
      ) : results.isError ? (
        // Quiet grey, no tone, no status role and no way to ask again: a
        // failed search read as a remark about the query rather than as the
        // search not having happened.
        <Panel>
          <p role="alert" className="flex flex-wrap items-center gap-2 px-4 py-4 text-small text-warn">
            <span className="min-w-0 flex-1">{explainLine(results.error, 'run this search')}</span>
            <button
              type="button"
              onClick={() => void results.refetch()}
              className="flex-none rounded border border-line bg-panel px-2.5 py-1 text-acc-2 transition-colors duration-fast hover:bg-hov"
            >
              Try again
            </button>
          </p>
        </Panel>
      ) : r!.availability !== 'AVAILABLE' ? (
        <Panel>
          <div className="flex flex-col gap-1 px-4 py-4">
            <div className="flex items-center gap-2">
              <Stamp tone="mut">{say(r!.availability)}</Stamp>
              <span className="text-row text-ink-2">No published records to search in this scope yet.</span>
            </div>
            <span className="text-small text-ink-3">
              The index fills as analysis publishes reconstructed records. Nothing is invented in the meantime.
            </span>
          </div>
        </Panel>
      ) : matches.length === 0 ? (
        <Panel>
          <p className="px-4 py-4 text-row text-ink-2">No records match “{q}”.</p>
        </Panel>
      ) : (
        <Panel>
          <PanelHeader
            title={`${r!.windowCount} matches`}
            sub={`${r!.windowTruncated ? `first ${r!.windowLimit} ranked · ` : ''}scored by ${r!.scoreDerivation.rule}@${r!.scoreDerivation.version}`}
          />
          {matches.map((m) => (
            <div key={m.recordRef} className="flex items-center gap-3 border-b border-line px-4 py-2.5 last:border-0">
              <ToolIcon tool={m.tool} />
              <span className="min-w-0 flex-1">
                <span className="block truncate text-row font-medium text-ink">{m.label}</span>
                {m.snippet && <span className="block truncate text-small text-ink-3">{String((m.snippet as { text?: string }).text ?? '')}</span>}
              </span>
              <span className="mono flex-none text-caption text-ink-4">{say(m.kind)}</span>
            </div>
          ))}
          {/* The server ranks and bounds a page and mints a cursor when more
              match. Saying so without offering the next page was the whole
              defect: the sentence named a remainder the page could reach and
              would not. */}
          <div className="flex flex-wrap items-center gap-3 border-t border-line px-4 py-2.5">
            {results.hasNextPage && (
              <Button variant="ghost" loading={results.isFetchingNextPage} onClick={() => void results.fetchNextPage()}>
                {results.isFetchingNextPage ? 'Reading…' : `Read the next ${PAGE}`}
              </Button>
            )}
            <span className="text-caption text-ink-3">
              {results.hasNextPage
                ? `${matches.length.toLocaleString()} read so far, ranked best first, and more match this search.`
                : `${matches.length.toLocaleString()} ${matches.length === 1 ? 'match' : 'matches'}, which is all of them.`}
            </span>
          </div>
        </Panel>
      )}
    </div>
  );
}
